import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Box, CircularProgress, Link, Typography } from "@mui/material";
import { Link as RouterLink, useParams, useSearchParams } from "react-router-dom";
import ScreenshotBoard from "../board/ScreenshotBoard";
import ScreenshotExpiredPage from "../components/ScreenshotExpiredPage";
import AppleArchiveMenu from "../components/AppleArchiveMenu";
import AppleChooseLevelDialog from "../components/AppleChooseLevelDialog";
import AppleDailyMenu from "../components/AppleDailyMenu";
import AppleHomePage from "../components/AppleHomePage";
import ApplePremiumPacksMenu from "../components/ApplePremiumPacksMenu";
import JigsawShowcaseGoogleAdCreative from "../components/JigsawShowcaseGoogleAdLayouts";
import { ApplePlayNavBar, ApplePlayToolbar } from "../components/ApplePlayChrome";
import ScreenshotPiecesTray from "../components/ScreenshotPiecesTray";
import ScreenshotDesignFrame from "../../../components/ScreenshotDesignFrame";
import { useScreenshotTranslation } from "../../../lib/screenshotI18n";
import { screenshotFontStack, screenshotShellFontSx } from "../../../lib/screenshotFonts";
import { markScreenshotNotReady, signalScreenshotSceneReady } from "../../../lib/screenshotReady";
import {
    getImageUrl,
    getStoryTitle,
    getTemplateGrid,
    loadScreenshotGallery,
    loadScreenshotJigsaw,
    loadScreenshotPremiumPacks,
} from "../lib/api";
import { isScreenshotSceneExpired } from "../lib/screenshotAccess";
import { computeScreenshotPieceLayout } from "../lib/screenshotPieceLayout";
import {
    getScreenshotScene,
    isAppleArchiveMenuScene,
    isAppleChooseLevelScene,
    isAppleDailyScene,
    isAppleHomeScene,
    isApplePremiumPacksScene,
    isScreenshotSceneConfigured,
} from "../screenshotScenes";
import { MAIN_PATH } from "../routes";
import { resolveScreenshotDevice } from "../../../components/ScreenshotDevicePicker";
import { getGrooveStyleForMat } from "../board/boardMatPalette";
import { jigsawMobileAccent, phoneShellSx } from "../styles/mainStyles";
import {
    androidDefaultMatHex,
    androidLightTokens,
} from "../styles/androidJigsawTokens";
import { jigsawScreenshotAccent, jigsawShellBgForScene } from "../styles/screenshotStyles";

const CAPTURE_FILL_SX = {
    width: "100%",
    height: "100%",
    minHeight: "100%",
    maxHeight: "100%",
    boxSizing: "border-box",
};

const playShellSx = {
    ...phoneShellSx,
    minHeight: "100%",
    height: "100%",
    pt: 0,
    background: "transparent",
};

function JigsawCaptureFrame({ scene, sx, children }) {
    return (
        <ScreenshotDesignFrame background={jigsawShellBgForScene(scene)}>
            <Box sx={{ ...sx, ...CAPTURE_FILL_SX }}>{children}</Box>
        </ScreenshotDesignFrame>
    );
}

/**
 * App Store screenshot — current Jigsaw Muse play screen (board + tray).
 * No promotional headlines. Google Ads still use the showcase creatives.
 */
export default function ScreenshotApplePlayPage() {
    const { slug } = useParams();
    const [searchParams] = useSearchParams();
    const device = resolveScreenshotDevice(searchParams.get("device"));
    const isSquareAd = searchParams.get("googleAdSquare") === "1";
    const isLandscapeAd = searchParams.get("googleAdLandscape") === "1";
    const isShowcaseAd = isSquareAd || isLandscapeAd;
    const { t, lang } = useScreenshotTranslation();
    const scene = getScreenshotScene(slug, device);
    const isArchive = isAppleArchiveMenuScene(scene);
    const isChooseLevel = isAppleChooseLevelScene(scene);
    const isHome = isAppleHomeScene(scene);
    const isDaily = isAppleDailyScene(scene);
    const isPremiumPacks = isApplePremiumPacksScene(scene);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [group, setGroup] = useState(null);
    const [template, setTemplate] = useState(null);
    const [galleryItems, setGalleryItems] = useState([]);
    const [premiumPacks, setPremiumPacks] = useState([]);
    const [boardScale, setBoardScale] = useState(1);
    // Android default mat "Off-White" (board/boardMat.js), flat like the native board.
    const playMat = { id: "off-white", hex: androidDefaultMatHex, surface: "matte" };
    const playGrooveStyle = getGrooveStyleForMat(playMat.hex);

    useEffect(() => {
        if (!isShowcaseAd || !scene) return undefined;
        markScreenshotNotReady();
        let cancelled = false;
        (async () => {
            await signalScreenshotSceneReady({
                fontFamily: screenshotFontStack(lang),
                lang,
            });
            if (cancelled) markScreenshotNotReady();
        })();
        return () => {
            cancelled = true;
        };
    }, [isShowcaseAd, scene, slug, lang]);

    useEffect(() => {
        if (isShowcaseAd) {
            setLoading(false);
            return undefined;
        }

        if (isScreenshotSceneExpired(slug)) {
            setLoading(false);
            return undefined;
        }

        if (!scene) {
            setError("Unknown screenshot scene");
            setLoading(false);
            return undefined;
        }

        if (!isScreenshotSceneConfigured(scene)) {
            setError("Screenshot scene is not configured yet");
            setLoading(false);
            return undefined;
        }

        let cancelled = false;
        (async () => {
            setLoading(true);
            setError(null);
            setGroup(null);
            setTemplate(null);
            setGalleryItems([]);
            setPremiumPacks([]);
            try {
                if (isArchive || isChooseLevel) {
                    const items = await loadScreenshotGallery(scene);
                    if (cancelled) return;
                    if (!items.length) {
                        setError("No puzzles found for this archive scene");
                        return;
                    }
                    setGalleryItems(items);
                    setError(null);
                    return;
                }

                if (isPremiumPacks) {
                    const packs = await loadScreenshotPremiumPacks(scene);
                    if (cancelled) return;
                    if (!packs.length) {
                        setError("No premium packs found for this scene");
                        return;
                    }
                    setPremiumPacks(packs);
                    setError(null);
                    return;
                }

                const result = await loadScreenshotJigsaw(scene);
                if (cancelled) return;
                setGroup(result.group);
                setTemplate(result.template);
                const grid = getTemplateGrid(result.template);
                if (!isHome && !isDaily && scene.expectedGrid && grid !== scene.expectedGrid) {
                    setError(
                        `Expected ${scene.expectedGrid}×${scene.expectedGrid} puzzle but loaded ${grid}×${grid}.`,
                    );
                } else {
                    setError(null);
                }
            } catch (e) {
                if (!cancelled) {
                    setError(e?.message ?? "Failed to load jigsaw");
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [scene, slug, isShowcaseAd, isArchive, isChooseLevel, isHome, isDaily, isPremiumPacks]);

    const grid = useMemo(() => (template ? getTemplateGrid(template) : 0), [template]);
    const paths = template?.piecePaths?.pieces ?? template?.piecePaths;
    const pieceList = Array.isArray(paths) ? paths : paths?.pieces;
    const imageUrl = getImageUrl(group?.story);
    const title = getStoryTitle(group?.story);
    const total = grid > 0 ? grid * grid : 0;
    const { boardPieceIndices, trayPieceIndices } = useMemo(
        () =>
            computeScreenshotPieceLayout(total, {
                emptyRatio: scene?.emptyRatio,
                emptyRatioMin: scene?.emptyRatioMin,
                emptyRatioMax: scene?.emptyRatioMax,
                emptyZone: scene?.emptyZone ?? "random",
                grid,
                seed: slug ?? "screenshot",
            }),
        [total, slug, scene?.emptyRatio, scene?.emptyRatioMin, scene?.emptyRatioMax, scene?.emptyZone, grid],
    );
    const showTray = trayPieceIndices.length > 0;
    // Android PlayBoard portrait tray ≈ 30% of play area (clamped). Wider iPad boards
    // need a taller reserve or tray pieces (scaled with the board) get clipped.
    const trayReservePx = device === "ipadPro13" ? 210 : 168;
    const trayBoxRef = useRef(null);
    const [trayBoxHeight, setTrayBoxHeight] = useState(0);

    useLayoutEffect(() => {
        if (!showTray) {
            setTrayBoxHeight(0);
            return undefined;
        }
        const el = trayBoxRef.current;
        if (!el) return undefined;
        const apply = () => setTrayBoxHeight(el.clientHeight || 0);
        apply();
        const observer = new ResizeObserver(apply);
        observer.observe(el);
        return () => observer.disconnect();
    }, [showTray, device, slug]);

    const galleryImageUrls = useMemo(
        () => galleryItems.map((item) => getImageUrl(item?.group?.story)).filter(Boolean),
        [galleryItems],
    );
    const premiumPackImageUrls = useMemo(
        () =>
            premiumPacks.flatMap((pack) =>
                (pack?.pictures || []).map((pic) => pic?.imageUrl).filter(Boolean),
            ),
        [premiumPacks],
    );

    useEffect(() => {
        markScreenshotNotReady();
    }, [slug, lang]);

    useEffect(() => {
        if (loading || error || isShowcaseAd) return undefined;
        if (isArchive || isChooseLevel) {
            if (!galleryItems.length || !galleryImageUrls.length) return undefined;
            let cancelled = false;
            (async () => {
                await signalScreenshotSceneReady({
                    imageUrls: galleryImageUrls,
                    fontFamily: screenshotFontStack(lang),
                    lang,
                });
                if (cancelled) markScreenshotNotReady();
            })();
            return () => {
                cancelled = true;
            };
        }
        if (isPremiumPacks) {
            if (!premiumPacks.length) return undefined;
            let cancelled = false;
            (async () => {
                await signalScreenshotSceneReady({
                    imageUrls: premiumPackImageUrls,
                    fontFamily: screenshotFontStack(lang),
                    lang,
                });
                if (cancelled) markScreenshotNotReady();
            })();
            return () => {
                cancelled = true;
            };
        }
        if (isHome || isDaily) {
            if (!imageUrl) return undefined;
            let cancelled = false;
            (async () => {
                await signalScreenshotSceneReady({
                    imageUrl,
                    fontFamily: screenshotFontStack(lang),
                    lang,
                });
                if (cancelled) markScreenshotNotReady();
            })();
            return () => {
                cancelled = true;
            };
        }
        if (!template || !pieceList?.length || !imageUrl || grid <= 0) {
            return undefined;
        }
        let cancelled = false;
        (async () => {
            await signalScreenshotSceneReady({
                imageUrl,
                fontFamily: screenshotFontStack(lang),
                lang,
            });
            if (cancelled) markScreenshotNotReady();
        })();
        return () => {
            cancelled = true;
        };
    }, [
        loading,
        error,
        isShowcaseAd,
        isArchive,
        isChooseLevel,
        isHome,
        isDaily,
        isPremiumPacks,
        galleryItems,
        galleryImageUrls,
        premiumPacks,
        premiumPackImageUrls,
        template,
        pieceList,
        imageUrl,
        grid,
        lang,
        slug,
    ]);

    if (isScreenshotSceneExpired(slug)) {
        return <ScreenshotExpiredPage />;
    }

    if (!scene) {
        return (
            <JigsawCaptureFrame scene={scene} sx={playShellSx}>
                <Box sx={{ p: 2 }}>
                    <Typography color="error">{t("mobileScreenshot.common.scene-not-found")}</Typography>
                    <Link component={RouterLink} to={MAIN_PATH} sx={{ color: jigsawScreenshotAccent }}>
                        {t("mobileScreenshot.common.back-to-index")}
                    </Link>
                </Box>
            </JigsawCaptureFrame>
        );
    }

    if (isShowcaseAd) {
        return (
            <Box sx={screenshotShellFontSx(lang)}>
                <JigsawShowcaseGoogleAdCreative
                    scene={scene}
                    lang={lang}
                    variant={isSquareAd ? "square" : "landscape"}
                />
            </Box>
        );
    }

    if (loading) {
        return (
            <JigsawCaptureFrame
                scene={scene}
                sx={{ ...playShellSx, alignItems: "center", justifyContent: "center" }}
            >
                <CircularProgress size={32} sx={{ color: jigsawMobileAccent }} />
            </JigsawCaptureFrame>
        );
    }

    if (
        error ||
        (isArchive || isChooseLevel
            ? !galleryItems.length
            : isPremiumPacks
              ? !premiumPacks.length
              : isHome || isDaily
                ? !imageUrl
                : !template || !pieceList?.length || !imageUrl || grid <= 0)
    ) {
        return (
            <JigsawCaptureFrame scene={scene} sx={playShellSx}>
                <Box sx={{ p: 2 }}>
                    <Typography color="error" sx={{ mb: 1 }}>
                        {error ?? t("mobileScreenshot.common.could-not-load")}
                    </Typography>
                    <Link component={RouterLink} to={MAIN_PATH} sx={{ color: jigsawScreenshotAccent }}>
                        {t("mobileScreenshot.common.back-to-index")}
                    </Link>
                </Box>
            </JigsawCaptureFrame>
        );
    }

    if (isArchive || isChooseLevel) {
        return (
            <JigsawCaptureFrame
                scene={scene}
                sx={{ ...playShellSx, ...screenshotShellFontSx(lang) }}
            >
                <Box sx={{ position: "relative", width: "100%", height: "100%", minHeight: 0 }}>
                    <AppleArchiveMenu items={galleryItems} />
                    {isChooseLevel ? <AppleChooseLevelDialog /> : null}
                </Box>
            </JigsawCaptureFrame>
        );
    }

    if (isPremiumPacks) {
        return (
            <JigsawCaptureFrame
                scene={scene}
                sx={{ ...playShellSx, ...screenshotShellFontSx(lang) }}
            >
                <ApplePremiumPacksMenu packs={premiumPacks} />
            </JigsawCaptureFrame>
        );
    }

    if (isHome) {
        return (
            <JigsawCaptureFrame
                scene={scene}
                sx={{ ...playShellSx, ...screenshotShellFontSx(lang) }}
            >
                <AppleHomePage
                    title={title}
                    imageUrl={imageUrl}
                    publishedLabel={scene.publishedLabel}
                    dark={scene.theme === "dark"}
                />
            </JigsawCaptureFrame>
        );
    }

    if (isDaily) {
        return (
            <JigsawCaptureFrame
                scene={scene}
                sx={{ ...playShellSx, ...screenshotShellFontSx(lang) }}
            >
                <AppleDailyMenu title={title} imageUrl={imageUrl} free />
            </JigsawCaptureFrame>
        );
    }

    return (
        <JigsawCaptureFrame
            scene={scene}
            sx={{
                ...playShellSx,
                ...screenshotShellFontSx(lang),
                bgcolor: androidLightTokens.shellBg,
            }}
        >
            {/* PlayScreen: nav row (back | title | gear + timer), then the PlayToolbar strip. */}
            <ApplePlayNavBar title={title} />
            <ApplePlayToolbar />
            {/* boardSlot: square board hugging the toolbar, tray filling the rest below it. */}
            <Box
                sx={{
                    flex: "1 1 auto",
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                    containerType: "size",
                    bgcolor: androidLightTokens.shellBg,
                }}
            >
                <Box
                    sx={{
                        flexShrink: 0,
                        alignSelf: "center",
                        position: "relative",
                        width: `min(100cqw, calc(100cqh - ${trayReservePx}px))`,
                        maxWidth: "100%",
                        aspectRatio: "1 / 1",
                        overflow: "hidden",
                    }}
                >
                    <ScreenshotBoard
                        grid={grid}
                        imageUrl={imageUrl}
                        paths={pieceList}
                        boardPieceIndices={boardPieceIndices}
                        svgPathFallback={template.svgPath}
                        onScaleChange={setBoardScale}
                        matPreset={playMat}
                        matColor={playMat.hex}
                        grooveStyle={playGrooveStyle}
                        fullBleed
                        showFullImage={Boolean(scene?.showFullImage)}
                    />
                </Box>
                {showTray ? (
                    <Box
                        ref={trayBoxRef}
                        sx={{
                            flex: "1 1 auto",
                            minHeight: trayReservePx,
                            mt: "4px",
                            overflow: "hidden",
                            bgcolor: androidLightTokens.trayBg,
                            borderTop: `1px solid ${androidLightTokens.trayBorder}`,
                            display: "flex",
                            alignItems: "center",
                            px: 1,
                            boxSizing: "border-box",
                        }}
                    >
                        <Box sx={{ width: "100%", height: "100%", minHeight: 0 }}>
                            <ScreenshotPiecesTray
                                pieceIndices={trayPieceIndices}
                                imageUrl={imageUrl}
                                grid={grid}
                                paths={pieceList}
                                boardScale={boardScale}
                                rows={2}
                                bare
                                maxHeight={trayBoxHeight > 0 ? trayBoxHeight : trayReservePx}
                            />
                        </Box>
                    </Box>
                ) : null}
            </Box>
        </JigsawCaptureFrame>
    );
}
